import React from 'react'
import {
  CertificateInput,
  PDF_INSTITUTION_FONT_SIZE,
  PDF_NAME_FONT_SIZE,
  PDF_NAME_SHADOW_OFFSET,
  PDF_PAGE_LONG_SIDE,
  PDF_TEXT_FONT_SIZE,
  formatCertificateDate,
  getCertificateBackgroundUrl,
  getCertificateOverlayLayout,
  getCertificateYear,
  getOverlayColor,
  overlayColorToCss
} from '../../utils/certificate'

interface CertificatePreviewProps {
  input: CertificateInput
  className?: string
}

/**
 * Converts a length in jsPDF page units to a share of the artwork's width, so overlay
 * text keeps the same proportions on screen that it has in the generated PDF.
 */
export const pdfPxToCqw = (pdfPx: number): string => `${pdfPx / PDF_PAGE_LONG_SIDE * 100}cqw`

const overlayStyle = (
  xPct: number,
  yPct: number,
  color: string,
  fontSize: number,
  extra?: React.CSSProperties
): React.CSSProperties => ({
  left: `${xPct}%`,
  top: `${yPct}%`,
  transform: 'translate(-50%, -50%)',
  fontSize: pdfPxToCqw(fontSize),
  color,
  ...extra
})

const CertificatePreview: React.FC<CertificatePreviewProps> = ({ input, className = '' }) => {
  const year = getCertificateYear(input.completedAt)
  const overlayCss = overlayColorToCss(getOverlayColor(year))
  const backgroundUrl = getCertificateBackgroundUrl(input.variant, year)
  const layout = getCertificateOverlayLayout(year)
  const formattedDate = formatCertificateDate(input.completedAt)
  const userName = `${input.firstName} ${input.lastName}`.toUpperCase()
  const showInstitution = input.variant === 'enterprise' &&
    typeof input.institutionName === 'string' &&
    input.institutionName.length > 0

  return (
    <div className={`relative inline-block max-w-full max-h-full ${className}`}>
      <img
        src={backgroundUrl}
        alt='Certificate of completion'
        className='max-w-full max-h-full w-auto h-auto min-w-0 min-h-0 object-scale-down'
      />
      {/* The overlay takes its width from the sized artwork above, which is what makes
          it usable as a query container — the artwork cannot size itself from content. */}
      <div
        className='absolute inset-0 pointer-events-none'
        style={{ containerType: 'inline-size' }}
      >
        <p
          data-testid='certificate-preview-name'
          className='absolute font-bold sentry-mask whitespace-nowrap'
          style={overlayStyle(layout.name.xPct, layout.name.yPct, overlayCss, PDF_NAME_FONT_SIZE, {
            textShadow: `0 ${pdfPxToCqw(PDF_NAME_SHADOW_OFFSET)} 0 rgb(153, 243, 207)`
          })}
        >
          {userName}
        </p>
        {showInstitution && (
          <p
            data-testid='certificate-preview-institution'
            className='absolute font-bold whitespace-nowrap'
            style={overlayStyle(layout.institution.xPct, layout.institution.yPct, overlayCss, PDF_INSTITUTION_FONT_SIZE)}
          >
            {input.institutionName?.toUpperCase()}
          </p>
        )}
        <p
          data-testid='certificate-preview-date'
          className='absolute font-bold whitespace-nowrap'
          style={overlayStyle(layout.date.xPct, layout.date.yPct, overlayCss, PDF_TEXT_FONT_SIZE)}
        >
          {formattedDate}
        </p>
        <p
          data-testid='certificate-preview-reference'
          className='absolute font-bold whitespace-nowrap'
          style={overlayStyle(layout.reference.xPct, layout.reference.yPct, overlayCss, PDF_TEXT_FONT_SIZE)}
        >
          {input.referenceNumber}
        </p>
      </div>
    </div>
  )
}

export default CertificatePreview
